/**
 * @file experienceFilters.component.tsx
 * @path apps/website/src/features/experiences/components/experienceFilters.component.tsx
 */
import { Search, SlidersHorizontal } from "lucide-react";
import { cn } from "@sirbastians/utils";
import { PriceBin } from "../hooks/useExperiencesFacade.hook";

interface ExperienceFiltersProps {
  categories: string[];
  activeCategory: string;
  setActiveCategory: (cat: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  itemCounts: Record<string, number>;
  maxPrice: number;
  absoluteMaxPrice: number;
  priceHistogram: PriceBin[];
  setMaxPrice: (price: number) => void;
  className?: string;
}

export const ExperienceFilters = ({
  categories,
  activeCategory,
  setActiveCategory,
  searchQuery,
  setSearchQuery,
  itemCounts,
  maxPrice,
  absoluteMaxPrice,
  priceHistogram,
  setMaxPrice,
  className,
}: ExperienceFiltersProps) => {
  // ✨ NEW: Map the current raw maxPrice back to a specific bin index for the slider
  let activeBinIndex = priceHistogram.findIndex((b) => maxPrice <= b.maxPrice);
  if (activeBinIndex === -1) activeBinIndex = priceHistogram.length - 1;

  return (
    <div className={cn("flex flex-col gap-10", className)}>
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="h-5 w-5 text-accent" />
        <h2 className="font-montserrat font-bold text-xl uppercase tracking-widest">
          Filters
        </h2>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search experiences..."
          className="w-full bg-secondary border border-border rounded-xl py-3 pl-10 pr-4 text-sm focus:ring-1 focus:ring-accent outline-none neuro-well transition-all"
        />
      </div>

      <div>
        <h3 className="font-montserrat font-bold text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-6">
          Categories
        </h3>
        <ul className="space-y-4">
          {categories.map((cat) => (
            <li
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={cn(
                "cursor-pointer text-sm transition-all flex justify-between items-center group py-1",
                activeCategory === cat
                  ? "text-foreground font-bold translate-x-1"
                  : "text-muted-foreground hover:text-foreground hover:translate-x-1",
              )}
            >
              <span className="flex items-center gap-2">
                <div
                  className={cn(
                    "h-1.5 w-1.5 rounded-full transition-all",
                    activeCategory === cat
                      ? "bg-accent scale-100"
                      : "bg-transparent scale-0",
                  )}
                />
                {cat}
              </span>
              <span className="text-[10px] font-mono opacity-50 bg-muted px-2 py-0.5 rounded-full">
                {itemCounts[cat] || 0}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-montserrat font-bold text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            Max Price
          </h3>
          <span className="text-xs font-mono font-bold text-accent">
            R {maxPrice.toLocaleString()}
          </span>
        </div>

        {/* ✨ UPDATED: Clickable Interactive Histogram Bars */}
        <div className="h-12 flex items-end gap-1 mb-2 px-1">
          {priceHistogram.map((bin, i) => {
            const isActive = maxPrice >= bin.maxPrice || i === activeBinIndex;
            return (
              <div
                key={i}
                onClick={() => setMaxPrice(bin.maxPrice)}
                title={`${bin.count} items up to R${Math.round(bin.maxPrice)}`}
                className={cn(
                  "flex-1 rounded-t-sm transition-all duration-300 cursor-pointer hover:brightness-110",
                  isActive ? "bg-accent" : "bg-muted neuro-inset",
                )}
                style={{ height: `${Math.max(bin.height, 4)}%` }}
              />
            );
          })}
        </div>

        {/* ✨ UPDATED: Slider mapped to Bin Index instead of linear raw price */}
        <input
          type="range"
          min={0}
          max={priceHistogram.length - 1}
          step={1}
          value={activeBinIndex}
          onChange={(e) =>
            setMaxPrice(priceHistogram[Number(e.target.value)].maxPrice)
          }
          className="w-full h-2 bg-secondary rounded-full appearance-none cursor-pointer neuro-inset accent-accent"
        />

        <div className="flex justify-between mt-4 text-[10px] font-mono text-muted-foreground">
          <span>R 0</span>
          <span>R {absoluteMaxPrice.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};
